import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useRouter } from "next/router";
import Head from "next/head";


const NAME_MAP = {
  personfirstname: "First Name",
  personlastname: "Last Name",
  personmiddleinitial: "Middle Initial",
  persongender: "Gender",
  persondateofbirthyear: "Birth Year",
  personexactage: "Age",
  email: "Email",
  email_address: "Email",
  email_score: "Email Score",
  emailflag: "Email Flag",
  areacode: "Area Code",
  phone: "Phone",
  phone_number: "Phone",
  wirelessphone: "Wireless Phone",
  donotcall: "Do Not Call",
  primaryaddress: "Address",
  secondaryaddress: "Address 2",
  cityname: "City",
  countyname: "County",
  state: "State",
  zipcode: "Zip Code",
  zip: "Zip",
  latitude: "Lat",
  longitude: "Lng",
  personmaritalstatus: "Marital Status",
  personeducation: "Education",
  personoccupation: "Occupation",
  estimatedincomecode: "Est. Income",
  networth: "Net Worth",
  creditrating: "Credit Rating",
  ethnicgroup: "Ethnic Group",
  languagecode: "Language",
  religioncode: "Religion",
  dwellingtype: "Dwelling",
  homeownerprobabilitymodel: "Homeowner Prob.",
  numberofpersonsinlivingunit: "Persons in Unit",
  numberofadults: "# Adults",
  presenceofchildren: "Has Children",
  numberofchildren: "# Children",
  investment: "Investment",
  presenceofcreditcard: "Has Credit Card",
  businessowner: "Business Owner",
  onlinepurchasingindicator: "Online Buyer",
  homepurchaseprice: "Home Price",
  homepurchasedateyear: "Home Purchase Year",
  homeyearbuilt: "Year Built",
  estimatedcurrenthomevaluecode: "Est. Home Value",
  mortgageamountinthousands: "Mortgage ($K)",
  mortgagelendername: "Mortgage Lender",
  lengthofresidence: "Years at Residence",
  investmentstocksecurities: "Stocks/Securities",
  investing_active: "Active Investor",
  investmentspersonal: "Personal Investments",
  newsandfinancial: "News & Financial",
  investingfinancegrouping: "Finance Group",
  creditcarduser: "Credit Card User",
  mailorderbuyer: "Mail Order Buyer",
  hightechleader: "High Tech Leader",
  computerowner: "Computer Owner",
  educationonline: "Online Education",
  investmentsrealestate: "Real Estate Inv.",
  investmentsforeign: "Foreign Inv.",
  investmentestimatedresidentialpropertiesowned: "# Properties Owned",
  presenceofgoldorplatinumcreditcard: "Gold/Platinum Card",
  presenceofpremiumcreditcard: "Premium Card",
  numberoflinesofcredit: "Lines of Credit",
  travelandentertainmentcardholder: "Travel Card",
  heavybusinesstravelers: "Heavy Traveler",
  careerimprovement: "Career Improvement",
  sohoindicator: "SOHO",
  career: "Career",
  professionalliving: "Professional Living",
  upscaleliving: "Upscale Living",
  highbrow: "Highbrow",
  high_tech_living: "High Tech Living",
  sportyliving: "Sporty Living",
  generationsinhousehold: "Generations",
  inferredhouseholdrank: "Household Rank",
  senioradultinhousehold: "Senior in HH",
  youngadultinhousehold: "Young Adult in HH",
  timezone: "Timezone",
  smoker: "Smoker",
  dietingweightloss: "Dieting",
  selfimprovement: "Self Improvement",
  healthmedical: "Health/Medical",
  bookreader: "Book Reader",
  traveler: "Traveler",
  golfenthusiasts: "Golf",
  outdoorenthusiast: "Outdoor",
  exerciseenthusiast: "Exercise",
  currentaffairspolitics: "Politics",
  politicalcontributor: "Political Donor",
  charitable: "Charitable",
  readinggeneral: "Reading",
  mailresponder: "Mail Responder",
  valuehunter: "Value Hunter",
  opportunityseekers: "Opportunity Seeker",
  automotivebuff: "Automotive",
  foodwines: "Food & Wine",
  consumerelectronics: "Electronics",
  gaming: "Gaming",
  telecommunications: "Telecom",
  censusmedianhomevalue: "Census Home Value",
  censusmedianhouseholdincome: "Census HH Income",
  first_name: "First Name",
  last_name: "Last Name",
  company_name: "Company",
  company: "Company",
  city: "City",
  name: "Full Name",
  original_id: "Original ID",
  industry: "Industry",
  sic_code_description: "Industry (SIC)",
  sic_description: "Industry (SIC)",
  website: "Website",
  url: "Website",
  web_address: "Website",
  domain: "Website",
  company_website: "Website",
};

function niceCol(col) {
  const lower = col.toLowerCase();
  if (NAME_MAP[lower]) return NAME_MAP[lower];
  return col
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmtN(n) {
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return n.toLocaleString();
}
function fmtBytes(b) {
  if (b >= 1024 * 1024 * 1024)
    return (b / 1024 / 1024 / 1024).toFixed(1) + " GB";
  if (b >= 1024 * 1024) return (b / 1024 / 1024).toFixed(0) + " MB";
  if (b >= 1024) return (b / 1024).toFixed(0) + " KB";
  return b + " B";
}
function getRowName(row) {
  if (!row) return "\u2014";
  const fn = row.personfirstname || row.first_name || row.firstname || "";
  const ln = row.personlastname || row.last_name || row.lastname || "";
  return (fn + " " + ln).trim() || row.name || "\u2014";
}
function getRowEmail(row) {
  return row?.email || row?.email_address || "\u2014";
}
function getRowPhone(row) {
  return row?.phone || row?.phone_number || row?.wirelessphone || "\u2014";
}
function getRowLoc(row) {
  const c = row?.cityname || row?.city || "";
  const s = row?.state || "";
  return c + (s ? ", " + s : "") || "\u2014";
}

const DEFAULTS = {
  business: [
    "first_name",
    "last_name",
    "email_address",
    "phone_number",
    "company_name",
    "company",
    // Industry — fall back through whichever column actually exists in the table
    "industry",
    "sic_code_description",
    "sic_description",
    // Website
    "website",
    "url",
    "web_address",
    "domain",
    "company_website",
    "city",
    "state",
    "zip",
  ],
  consumer: [
    "personfirstname",
    "personlastname",
    "email",
    "phone",
    "wirelessphone",
    "cityname",
    "state",
    "zipcode",
    "persongender",
    "estimatedincomecode",
    "networth",
    "creditrating",
  ],
};
const COPY_COLS = new Set([
  "email",
  "email_address",
  "phone",
  "phone_number",
  "wirelessphone",
  "areacode",
  "primaryaddress",
  "zipcode",
  "zip",
]);

function emailCol(cols) {
  return cols.filter((c) => c === "email" || c === "email_address");
}
function nameEmailCols(cols) {
  const w = new Set([
    "first_name",
    "last_name",
    "personfirstname",
    "personlastname",
    "name",
    "email",
    "email_address",
  ]);
  return cols.filter((c) => w.has(c));
}
function nameEmailPhoneCols(cols) {
  const w = new Set([
    "first_name",
    "last_name",
    "personfirstname",
    "personlastname",
    "name",
    "email",
    "email_address",
    "phone",
    "phone_number",
    "wirelessphone",
  ]);
  return cols.filter((c) => w.has(c));
}
function nameEmailPhoneLocCols(cols) {
  const w = new Set([
    "first_name",
    "last_name",
    "personfirstname",
    "personlastname",
    "name",
    "email",
    "email_address",
    "phone",
    "phone_number",
    "wirelessphone",
    "primaryaddress",
    "address",
    "city",
    "cityname",
    "state",
    "zipcode",
    "zip",
    "countyname",
  ]);
  return cols.filter((c) => w.has(c));
}

function Toast({ msg }) {
  return msg ? <div className="toast">{msg}</div> : null;
}

function HL({ text, term }) {
  if (!term || !text) return <>{text || "\u2014"}</>;
  const s = String(text),
    i = s.toLowerCase().indexOf(term.toLowerCase());
  if (i === -1) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark>{s.slice(i, i + term.length)}</mark>
      {s.slice(i + term.length)}
    </>
  );
}

/* ═══ DETAIL PANEL ═══ */
function DetailPanel({ row, columns, onClose, onToast }) {
  const filled = columns.filter((c) => row[c] && String(row[c]).trim()).length;
  function copyAll() {
    navigator.clipboard.writeText(
      columns.map((c) => niceCol(c) + ": " + (row[c] || "")).join("\n"),
    );
    onToast("All fields copied!");
  }
  return (
    <div className="detail-panel">
      <div className="dp-header">
        <div>
          <h3>{getRowName(row)}</h3>
          <div style={{ fontSize: 12, color: "var(--tx-3)", marginTop: 2 }}>
            {filled}/{columns.length} fields filled
          </div>
        </div>
        <button className="dp-close" onClick={onClose}>
          {"\u2715"}
        </button>
      </div>
      <div className="dp-actions">
        <button className="b b-ac" onClick={copyAll}>
          {"\uD83D\uDCCB"} Copy All
        </button>
        {(row.email || row.email_address) && (
          <button
            className="b b-grn"
            onClick={() => {
              navigator.clipboard.writeText(row.email || row.email_address);
              onToast("Email copied!");
            }}
          >
            {"\uD83D\uDCE7"} Email
          </button>
        )}
        {(row.phone || row.phone_number || row.wirelessphone) && (
          <button
            className="b b-blue"
            onClick={() => {
              navigator.clipboard.writeText(
                row.phone || row.phone_number || row.wirelessphone || "",
              );
              onToast("Phone copied!");
            }}
          >
            {"\uD83D\uDCDE"} Phone
          </button>
        )}
        {(row.primaryaddress || row.address) && (
          <button
            className="b b-gray"
            onClick={() => {
              navigator.clipboard.writeText(
                [
                  row.primaryaddress || row.address,
                  row.cityname || row.city,
                  row.state,
                  row.zipcode || row.zip,
                ]
                  .filter(Boolean)
                  .join(", "),
              );
              onToast("Address copied!");
            }}
          >
            {"\uD83D\uDCCD"} Address
          </button>
        )}
      </div>
      <div className="dp-body">
        {columns.map((c) => {
          const v = row[c],
            has = v && String(v).trim();
          return (
            <div className="dp-row" key={c} style={{ opacity: has ? 1 : 0.35 }}>
              <span className="dp-label">{niceCol(c)}</span>
              <span
                className="dp-val"
                style={{ cursor: has ? "pointer" : "default" }}
                onClick={() => {
                  if (has) {
                    navigator.clipboard.writeText(String(v));
                    onToast("Copied!");
                  }
                }}
                title={has ? "Click to copy" : ""}
              >
                {v || "\u2014"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ COLUMN DROPDOWN with search ═══ */
function ColDD({ allCols, visCols, setVisCols }) {
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.focus();
  }, []);
  function toggle(c) {
    const s = new Set(visCols);
    if (s.has(c)) {
      if (s.size > 1) s.delete(c);
    } else s.add(c);
    setVisCols(s);
  }
  const filtered = search.trim()
    ? allCols.filter(
        (c) =>
          niceCol(c).toLowerCase().includes(search.toLowerCase()) ||
          c.includes(search.toLowerCase()),
      )
    : allCols;
  return (
    <div className="dd" onClick={(e) => e.stopPropagation()}>
      <div className="dd-head">
        <span>
          Columns ({visCols.size}/{allCols.length})
        </span>
        <div className="dd-btns">
          <button
            className="dd-btn"
            onClick={() => setVisCols(new Set(allCols))}
          >
            All
          </button>
          <button
            className="dd-btn"
            onClick={() => {
              const m = allCols.filter((c) =>
                [
                  "email",
                  "email_address",
                  "first_name",
                  "last_name",
                  "personfirstname",
                  "personlastname",
                  "name",
                ].includes(c),
              );
              setVisCols(new Set(m.length > 0 ? m : allCols.slice(0, 3)));
            }}
          >
            Minimal
          </button>
        </div>
      </div>
      <div
        style={{ padding: "8px 14px", borderBottom: "1px solid var(--border)" }}
      >
        <input
          ref={ref}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search columns..."
          style={{
            width: "100%",
            height: 32,
            padding: "0 10px",
            background: "var(--bg-2)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            color: "var(--tx-1)",
            fontSize: 13,
            fontFamily: "Outfit",
            outline: "none",
          }}
        />
      </div>
      <div className="dd-list">
        {filtered.length === 0 && (
          <div
            style={{ padding: "12px 14px", color: "var(--tx-4)", fontSize: 13 }}
          >
            No matching columns
          </div>
        )}
        {filtered.map((c) => (
          <div
            key={c}
            className={"dd-item " + (visCols.has(c) ? "on" : "")}
            onClick={() => toggle(c)}
          >
            <input type="checkbox" checked={visCols.has(c)} readOnly />
            <span style={{ flex: 1 }}>{niceCol(c)}</span>
            <span
              style={{
                fontSize: 10,
                color: "var(--tx-4)",
                fontFamily: "JetBrains Mono",
              }}
            >
              {c}
            </span>
          </div>
        ))}
      </div>
      <div
        style={{
          padding: "8px 14px",
          borderTop: "1px solid var(--border)",
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <button
          className="dd-btn"
          onClick={() => {
            const w = allCols.filter((c) =>
              [
                "email",
                "email_address",
                "phone",
                "phone_number",
                "wirelessphone",
                "personfirstname",
                "personlastname",
                "first_name",
                "last_name",
                "name",
              ].includes(c),
            );
            setVisCols(new Set(w));
          }}
        >
          Contact
        </button>
        <button
          className="dd-btn"
          onClick={() => {
            const w = allCols.filter((c) =>
              [
                "primaryaddress",
                "address",
                "cityname",
                "city",
                "countyname",
                "state",
                "zipcode",
                "zip",
                "latitude",
                "longitude",
              ].includes(c),
            );
            setVisCols(new Set(w.length > 0 ? w : allCols.slice(0, 5)));
          }}
        >
          Location
        </button>
        <button
          className="dd-btn"
          onClick={() => {
            const w = allCols.filter((c) =>
              [
                "estimatedincomecode",
                "networth",
                "creditrating",
                "homepurchaseprice",
                "investment",
                "presenceofcreditcard",
                "investmentstocksecurities",
              ].includes(c),
            );
            setVisCols(new Set(w.length > 0 ? w : allCols.slice(0, 5)));
          }}
        >
          Financial
        </button>
      </div>
    </div>
  );
}

/* ═══ FILTERS POPUP — dedicated overlay with all the filter inputs ═══ */
function FiltersPopup({
  table, columns,
  searchCol, setSearchCol,
  stateF, setStateF,
  cityF, setCityF,
  industryF, setIndustryF,
  naicsF, setNaicsF,
  onClose, onClearAll,
}) {
  const activeCount = [searchCol, stateF, cityF, industryF, naicsF].filter(Boolean).length;
  return (
    <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ep" style={{ position: 'relative', maxWidth: 560 }}>
        <div className="ep-h">
          <h3>{"\uD83D\uDD0E"} Filters {activeCount > 0 && (
            <span style={{
              marginLeft: 8, padding: '2px 8px', borderRadius: 10,
              background: 'rgba(96,165,250,.18)', color: 'var(--blue)',
              fontSize: 12, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
            }}>{activeCount} active</span>
          )}</h3>
          <button className="ep-x" onClick={onClose}>{"\u2715"}</button>
        </div>
        <div className="ep-b">
          <div className="ep-s">
            <div className="ep-st" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Filter the visible rows</span>
              {activeCount > 0 && (
                <button
                  onClick={onClearAll}
                  style={{
                    background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                    padding: '3px 10px', fontSize: 11, color: 'var(--tx-3)', cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {"\u2715"} Clear All
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              {/* Search column dropdown */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Search Column (which column the search bar matches)</label>
                <select
                  value={searchCol}
                  onChange={(e) => setSearchCol(e.target.value)}
                  style={{
                    width: '100%', height: 38, padding: '0 12px', marginTop: 4,
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                    fontFamily: 'inherit', outline: 'none',
                  }}
                >
                  <option value="">All searchable columns</option>
                  {columns.map((c) => (
                    <option key={c} value={c}>{niceCol(c)}</option>
                  ))}
                </select>
              </div>
              {/* State */}
              <div>
                <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>State</label>
                <input
                  type="text"
                  value={stateF}
                  onChange={(e) => setStateF(e.target.value.toUpperCase().slice(0, 50))}
                  placeholder="CA, NY, TX..."
                  style={{
                    width: '100%', height: 38, padding: '0 12px', marginTop: 4,
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                    fontFamily: 'inherit', outline: 'none', textTransform: 'uppercase',
                  }}
                />
              </div>
              {/* City */}
              <div>
                <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>City</label>
                <input
                  type="text"
                  value={cityF}
                  onChange={(e) => setCityF(e.target.value)}
                  placeholder="Dallas, Miami..."
                  style={{
                    width: '100%', height: 38, padding: '0 12px', marginTop: 4,
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                    fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
              {table === 'business' && (
                <div>
                  <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Industry</label>
                  <input
                    type="text"
                    value={industryF}
                    onChange={(e) => setIndustryF(e.target.value)}
                    placeholder="Tech, Healthcare..."
                    style={{
                      width: '100%', height: 38, padding: '0 12px', marginTop: 4,
                      background: 'var(--bg-2)', border: '1px solid var(--border)',
                      borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                      fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                </div>
              )}
              {table === 'business' && (
                <div>
                  <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>NAICS Code</label>
                  <input
                    type="text"
                    value={naicsF}
                    onChange={(e) => setNaicsF(e.target.value)}
                    placeholder="722511 (full or partial)"
                    style={{
                      width: '100%', height: 38, padding: '0 12px', marginTop: 4,
                      background: 'var(--bg-2)', border: '1px solid var(--border)',
                      borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                      fontFamily: 'JetBrains Mono, monospace', outline: 'none',
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 12, lineHeight: 1.5 }}>
              For multi-select industries with row-count autocomplete, click <b>Export</b> in the toolbar — the export popup has the full filter UI.
            </div>
          </div>
        </div>
        <div className="ep-f">
          <div className="ep-sum">
            {activeCount === 0 ? "No filters applied" : `${activeCount} filter${activeCount === 1 ? '' : 's'} applied`}
          </div>
          <button className="ep-dl" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══ EXPORT PANEL — persistent batching with auto-advance ═══ */
function ExportPanel({
  table,
  columns,
  totalMatch,
  filters,
  pageSize: ps,
  onClose,
  onToast,
}) {
  const storageKey = `export_progress_${table}`;

  function loadProgress() {
    try {
      const s = localStorage.getItem(storageKey);
      if (s) return JSON.parse(s);
    } catch (e) {}
    return null;
  }
  function saveProgress(offset, log) {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ offset, log, savedAt: new Date().toISOString() }));
    } catch (e) {}
  }

  const saved = loadProgress();
  const [selCols, setSelCols] = useState(new Set(columns));
  const [batchSize, setBatchSize] = useState(50000);
  const [customBatch, setCustomBatch] = useState("");
  const [currentOffset, setCurrentOffset] = useState(saved ? saved.offset : 0);
  const [log, setLog] = useState(saved && saved.log ? saved.log : []);
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [colSearch, setColSearch] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSuccess, setShowSuccess] = useState(null);
  const [showHowTo, setShowHowTo] = useState(() => {
    try { return !localStorage.getItem("export_seen_howto"); } catch (e) { return true; }
  });

  // Filter overrides — start from the page's current filters but let user
  // change them inside the popup so they don't have to leave it.
  const [localQ, setLocalQ] = useState(filters.q || "");
  const [localState, setLocalState] = useState(filters.state || "");
  const [localCity, setLocalCity] = useState(filters.city || "");
  // MULTI-SELECT industries (array of strings). Initial entry comes from the
  // parent's single-industry filter so popup doesn't lose context.
  const [localIndustries, setLocalIndustries] = useState(() =>
    filters.industry ? [filters.industry] : []
  );
  const [localNaics, setLocalNaics] = useState(filters.naics || "");
  const [industryInput, setIndustryInput] = useState("");
  const [localTotalMatch, setLocalTotalMatch] = useState(totalMatch);
  const [recountLoading, setRecountLoading] = useState(false);

  // Industry autocomplete state
  const [industriesList, setIndustriesList] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);

  const filterDebRef = useRef(null);
  const industryDebRef = useRef(null);
  const recountAbortRef = useRef(null);
  const [totalIsExact, setTotalIsExact] = useState(true);
  const [countFailed, setCountFailed] = useState(false);
  const lastQueriedRef = useRef(JSON.stringify({
    q: filters.q || "", state: filters.state || "", city: filters.city || "",
    industries: (filters.industry ? [filters.industry] : []).slice().sort(),
    naics: ""
  }));

  // Refetch total count when ANY of the local filters change.
  // - resets numbers IMMEDIATELY so user never sees stale unfiltered total
  // - cancels any in-flight recount via AbortController so rapid filter
  //   changes don't pile up requests in the connection pool
  useEffect(() => {
    const sig = JSON.stringify({
      q: localQ, state: localState, city: localCity,
      industries: localIndustries.slice().sort(), naics: localNaics
    });
    if (sig === lastQueriedRef.current) return;
    lastQueriedRef.current = sig;

    // Cancel any in-flight recount
    if (recountAbortRef.current) recountAbortRef.current.abort();

    // Wipe stale numbers right now
    setRecountLoading(true);
    setCountFailed(false);
    setLocalTotalMatch(0);
    setCurrentOffset(0);

    if (filterDebRef.current) clearTimeout(filterDebRef.current);
    filterDebRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      recountAbortRef.current = ctrl;
      try {
        const pp = new URLSearchParams({
          table,
          q: localQ,
          searchCol: filters.searchCol || "",
          state: localState,
          city: localCity,
          industries: localIndustries.join(","),
          naics: localNaics,
          sortBy: filters.sortBy || "id",
          sortDir: filters.sortDir || "asc",
          limit: "1",
          offset: "0",
        });
        const r = await fetch("/api/export-preview?" + pp, { signal: ctrl.signal });
        if (r.ok) {
          const d = await r.json();
          if (d.countFailed) {
            setCountFailed(true);
            setLocalTotalMatch(0);
          } else {
            setLocalTotalMatch(d.totalMatch || 0);
            setTotalIsExact(d.totalIsExact !== false);
          }
        } else {
          // Server error — let user export anyway
          setCountFailed(true);
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          setCountFailed(true);
        }
      } finally {
        if (recountAbortRef.current === ctrl) {
          setRecountLoading(false);
          recountAbortRef.current = null;
        }
      }
    }, 400);
    return () => { if (filterDebRef.current) clearTimeout(filterDebRef.current); };
  }, [localQ, localState, localCity, localIndustries, localNaics, table]);

  // Load industry suggestions (debounced) — fires when dropdown opens OR
  // when the user types in the autocomplete box
  useEffect(() => {
    if (table !== 'business') return;
    if (!showIndustryDropdown) return;
    if (industryDebRef.current) clearTimeout(industryDebRef.current);
    industryDebRef.current = setTimeout(async () => {
      setIndustriesLoading(true);
      try {
        const pp = new URLSearchParams({ table, q: industryInput });
        const r = await fetch("/api/industries?" + pp);
        if (r.ok) {
          const d = await r.json();
          setIndustriesList(d.industries || []);
        }
      } catch (e) {} finally {
        setIndustriesLoading(false);
      }
    }, 200);
    return () => { if (industryDebRef.current) clearTimeout(industryDebRef.current); };
  }, [industryInput, showIndustryDropdown, table]);

  // Close industry dropdown when clicking outside
  useEffect(() => {
    if (!showIndustryDropdown) return;
    function onDocClick(e) {
      if (e.target.closest('.industry-picker')) return;
      setShowIndustryDropdown(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [showIndustryDropdown]);

  function addIndustry(name) {
    if (!name) return;
    setLocalIndustries((prev) => prev.includes(name) ? prev : [...prev, name]);
    setIndustryInput("");
  }
  function removeIndustry(name) {
    setLocalIndustries((prev) => prev.filter((i) => i !== name));
  }
  function clearAllLocalFilters() {
    setLocalQ("");
    setLocalState("");
    setLocalCity("");
    setLocalIndustries([]);
    setLocalNaics("");
    setIndustryInput("");
  }
  const hasActiveLocalFilters = !!(
    localQ || localState || localCity || localIndustries.length > 0 || localNaics
  );

  // Persist whenever offset or log changes
  useEffect(() => { saveProgress(currentOffset, log); }, [currentOffset, log]);

  function toggleCol(c) {
    const s = new Set(selCols);
    if (s.has(c)) s.delete(c);
    else s.add(c);
    setSelCols(s);
  }

  const batchPresets = [10000, 25000, 50000, 100000, 250000];
  const effBatch = customBatch ? parseInt(customBatch) || 50000 : batchSize;
  const effTotal = localTotalMatch;
  const rem = Math.max(0, effTotal - currentOffset);
  const actual = Math.min(effBatch, rem);
  const totalDL = log.reduce((s, h) => s + h.rows, 0);
  const allDone = rem <= 0;
  const batchNum = log.length + 1;
  const pct = effTotal > 0 ? Math.round(currentOffset / effTotal * 100) : 0;
  const batchesLeft = actual > 0 ? Math.ceil(rem / effBatch) : 0;
  const estSize = actual * selCols.size * 100;

  const filteredCols = colSearch.trim()
    ? columns.filter(c => niceCol(c).toLowerCase().includes(colSearch.toLowerCase()) || c.includes(colSearch.toLowerCase()))
    : columns;

  async function handleDL(opts = {}) {
    const exportAll = !!opts.all;
    if (selCols.size === 0) return;
    if (!exportAll && actual <= 0) return;
    setDownloading(true);

    const dlLimit = exportAll ? 'all' : String(effBatch);
    const dlOffset = exportAll ? 0 : currentOffset;
    const dlRows = exportAll ? effTotal : actual;

    // Build the shared filter payload — used for BOTH preview and download
    // so what you see in the totals is exactly what you get in the CSV.
    const filterPayload = {
      table,
      q: localQ,
      searchCol: filters.searchCol || "",
      state: localState,
      city: localCity,
      industries: localIndustries.join(","),
      naics: localNaics,
      sortBy: filters.sortBy || "id",
      sortDir: filters.sortDir || "asc",
    };

    // Fetch preview (first/last row)
    let firstRow = null, lastRow = null;
    try {
      const pp = new URLSearchParams({
        ...filterPayload,
        limit: exportAll ? String(effTotal) : String(effBatch),
        offset: String(dlOffset),
      });
      const pr = await fetch("/api/export-preview?" + pp);
      if (pr.ok) {
        const p = await pr.json();
        firstRow = p.firstRow;
        lastRow = p.lastRow;
      }
    } catch (e) {
      console.warn("Preview fetch failed:", e);
    }

    // Trigger the CSV download — same filter payload as the preview
    const params = new URLSearchParams({
      ...filterPayload,
      columns: Array.from(selCols).join(","),
      limit: dlLimit, offset: String(dlOffset),
    });
    const a = document.createElement("a");
    a.href = "/api/export?" + params;
    a.download = exportAll
      ? `${table}_export_ALL_${dlRows}_rows.csv`
      : `batch_${batchNum}_rows_${currentOffset + 1}_to_${currentOffset + actual}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Add to log
    const newLog = [{
      id: Date.now(), batch: exportAll ? "ALL" : batchNum, cols: selCols.size,
      colList: Array.from(selCols).slice(0, 5).map(c => niceCol(c)),
      rows: dlRows, from: dlOffset, to: dlOffset + dlRows,
      firstRow: firstRow ? { name: getRowName(firstRow), email: getRowEmail(firstRow), phone: getRowPhone(firstRow), loc: getRowLoc(firstRow) } : null,
      lastRow: lastRow ? { name: getRowName(lastRow), email: getRowEmail(lastRow), phone: getRowPhone(lastRow), loc: getRowLoc(lastRow) } : null,
      time: new Date().toLocaleTimeString(), date: new Date().toLocaleDateString(),
      estSize: fmtBytes(dlRows * selCols.size * 100),
    }, ...log];

    setLog(newLog);
    // AUTO-ADVANCE to next batch (skip when exporting all)
    let newOffset = currentOffset;
    if (!exportAll) {
      newOffset = currentOffset + actual;
      setCurrentOffset(newOffset);
    } else {
      newOffset = effTotal;
      setCurrentOffset(effTotal);
    }

    // Show success animation
    const isDone = newOffset >= effTotal;
    setShowSuccess({
      rows: dlRows,
      batch: exportAll ? "ALL" : batchNum,
      done: isDone,
      remaining: Math.max(0, effTotal - newOffset),
    });
    setTimeout(() => setShowSuccess(null), 2500);

    onToast(exportAll
      ? `✅ Full export started — ${dlRows.toLocaleString()} rows streaming`
      : `✅ Batch #${batchNum} downloaded — ${actual.toLocaleString()} rows`);
    setTimeout(() => setDownloading(false), 1500);

    // Dismiss how-to after first download
    if (showHowTo) {
      setShowHowTo(false);
      try { localStorage.setItem("export_seen_howto", "1"); } catch (e) {}
    }
  }

  function resetProgress() {
    if (confirm("Start over from row 1? This clears your download history.")) {
      setCurrentOffset(0);
      setLog([]);
      try { localStorage.removeItem(storageKey); } catch (e) {}
    }
  }

  return (
    <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ep" style={{ position: "relative" }}>
        <div className="ep-h">
          <h3>{"\u2B07"} Export Data</h3>
          <button className="ep-x" onClick={onClose}>{"\u2715"}</button>
        </div>
        <div className="ep-b">

          {/* SUCCESS FLASH */}
          {showSuccess && (
            <div style={{
              position: "absolute", inset: 0, background: "rgba(52,211,153,.12)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              zIndex: 10, borderRadius: 16, pointerEvents: "none",
              animation: "successPulse 2s ease forwards",
            }}>
              <div style={{ fontSize: 52, animation: "successBounce .5s ease" }}>
                {showSuccess.done ? "\uD83C\uDF89" : "\u2705"}
              </div>
              <div style={{ fontFamily: "Sora, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--grn)", marginTop: 8 }}>
                {showSuccess.done ? "All Done!" : `Batch #${showSuccess.batch} Downloaded!`}
              </div>
              <div style={{ fontSize: 13, color: "var(--tx-2)", marginTop: 4 }}>
                {showSuccess.rows.toLocaleString()} rows saved{showSuccess.done ? "" : ` · ${showSuccess.remaining.toLocaleString()} remaining`}
              </div>
            </div>
          )}

          {/* HOW IT WORKS — first time only */}
          {showHowTo && currentOffset === 0 && (
            <div style={{
              background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 10,
              padding: "14px 18px", marginBottom: 16,
            }}>
              <div
                style={{ fontSize: 12, fontWeight: 600, color: "var(--tx-3)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                onClick={() => { setShowHowTo(false); try { localStorage.setItem("export_seen_howto", "1"); } catch (e) {} }}
              >
                {"\uD83D\uDCA1"} How This Works
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--tx-4)" }}>{"\u2715"} dismiss</span>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                {[
                  ["1", "Pick batch size\n(e.g. 50K rows)"],
                  ["2", "Click download —\nit auto-advances"],
                  ["3", "Come back anytime,\nit remembers your spot"],
                ].map(([n, label]) => (
                  <div key={n} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 4 }}>
                    <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--ac-glow)", color: "var(--ac)", borderRadius: "50%", fontSize: 13, fontWeight: 700, fontFamily: "JetBrains Mono, monospace" }}>{n}</div>
                    <div style={{ fontSize: 12, color: "var(--tx-2)", lineHeight: 1.4, whiteSpace: "pre-line" }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PROGRESS BAR */}
          {(currentOffset > 0 || log.length > 0) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ height: 24, background: "var(--bg-3)", borderRadius: 12, overflow: "hidden", position: "relative", border: "1px solid var(--border)" }}>
                <div style={{
                  height: "100%", background: "linear-gradient(90deg, rgba(52,211,153,.5), var(--grn))",
                  borderRadius: 12, transition: "width .6s cubic-bezier(.4,0,.2,1)",
                  width: Math.max(pct, 1) + "%",
                }} />
                <div style={{
                  position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, fontFamily: "JetBrains Mono, monospace", color: "var(--tx-1)",
                  textShadow: "0 1px 3px rgba(0,0,0,.5)", zIndex: 1,
                }}>
                  {allDone ? "100% Complete!" : `${pct}% — ${fmtN(currentOffset)} of ${fmtN(effTotal)} rows`}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: "var(--tx-3)", fontFamily: "JetBrains Mono, monospace" }}>
                <span>Downloaded: {fmtN(totalDL)} rows in {log.length} batch{log.length !== 1 ? "es" : ""}</span>
                <span>{allDone ? "\u2705 Complete" : `${batchesLeft} batch${batchesLeft !== 1 ? "es" : ""} left`}</span>
              </div>
            </div>
          )}

          {/* RESUME BANNER */}
          {currentOffset > 0 && !allDone && (
            <div style={{ background: "rgba(96,165,250,.08)", border: "2px solid rgba(96,165,250,.3)", borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--blue)", marginBottom: 6, fontFamily: "Sora, sans-serif" }}>
                {"\uD83D\uDCE5"} Pick Up Where You Left Off
              </div>
              <div style={{ fontSize: 14, color: "var(--tx-2)", lineHeight: 1.7 }}>
                Rows <b style={{ color: "var(--tx-1)", fontFamily: "JetBrains Mono, monospace" }}>1 {"\u2192"} {fmtN(currentOffset)}</b> already downloaded.<br />
                Next batch starts at row <b style={{ color: "var(--grn)", fontFamily: "JetBrains Mono, monospace" }}>{fmtN(currentOffset + 1)}</b>.<br />
                <span style={{ color: "var(--tx-3)" }}>{fmtN(rem)} rows remaining · ~{batchesLeft} more batch{batchesLeft !== 1 ? "es" : ""}.</span>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button className="b b-gray" onClick={resetProgress} style={{ fontSize: 12 }}>{"\uD83D\uDD04"} Start Over From Row 1</button>
              </div>
            </div>
          )}

          {/* ALL DONE BANNER */}
          {allDone && (
            <div style={{ background: "rgba(52,211,153,.08)", border: "2px solid rgba(52,211,153,.3)", borderRadius: 12, padding: 20, marginBottom: 16, textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{"\uD83C\uDF89"}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "var(--grn)", fontFamily: "Sora, sans-serif" }}>
                All {fmtN(effTotal)} Rows Downloaded!
              </div>
              <div style={{ fontSize: 13, color: "var(--tx-3)", marginTop: 6 }}>
                Downloaded in {log.length} batch{log.length !== 1 ? "es" : ""}. You're all set!
              </div>
              <button className="b b-gray" onClick={resetProgress} style={{ marginTop: 12, fontSize: 13 }}>
                {"\uD83D\uDD04"} Start Over From Row 1
              </button>
            </div>
          )}

          {/* TOTAL RECORDS */}
          <div className="ep-s">
            <div className="ep-st">
              Total Records
              <span className="ep-match">
                {recountLoading
                  ? "..."
                  : countFailed
                    ? "many (count timed out)"
                    : (totalIsExact ? fmtN(effTotal) : fmtN(effTotal) + "+") + " rows"}
              </span>
            </div>
            {hasActiveLocalFilters && (
              <div style={{ fontSize: 12, color: "var(--tx-4)", marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span>Filtered by:</span>
                {localQ && <span style={{ color: "var(--ac)" }}>&quot;{localQ}&quot;</span>}
                {localState && <span style={{ color: "var(--grn)" }}>state={localState}</span>}
                {localCity && <span style={{ color: "var(--blue)" }}>city={localCity}</span>}
                {localNaics && <span style={{ color: "#a78bfa" }}>naics={localNaics}</span>}
                {localIndustries.length > 0 && (
                  <span style={{ color: "#f59e0b" }}>
                    industries={localIndustries.length === 1 ? localIndustries[0] : `${localIndustries.length} selected`}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* FILTERS — applied to both preview count AND export download */}
          <div className="ep-s">
            <div className="ep-st" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{"\uD83D\uDD0E"} Filters {hasActiveLocalFilters && <span style={{ color: 'var(--ac)', fontSize: 11, marginLeft: 6 }}>● applied to export</span>}</span>
              {hasActiveLocalFilters && (
                <button
                  onClick={clearAllLocalFilters}
                  style={{
                    background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                    padding: '3px 10px', fontSize: 11, color: 'var(--tx-3)', cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {"\u2715"} Clear All
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              {/* Search query */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Search (name, email, company, address)</label>
                <input
                  type="text"
                  value={localQ}
                  onChange={(e) => setLocalQ(e.target.value)}
                  placeholder="e.g. John Smith, acme.com, 555-1234..."
                  style={{
                    width: '100%', height: 36, padding: '0 12px', marginTop: 4,
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                    fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
              {/* State */}
              <div>
                <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>State</label>
                <input
                  type="text"
                  value={localState}
                  onChange={(e) => setLocalState(e.target.value.toUpperCase().slice(0, 50))}
                  placeholder="CA, NY, TX..."
                  style={{
                    width: '100%', height: 36, padding: '0 12px', marginTop: 4,
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                    fontFamily: 'inherit', outline: 'none', textTransform: 'uppercase',
                  }}
                />
              </div>
              {/* City */}
              <div>
                <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>City</label>
                <input
                  type="text"
                  value={localCity}
                  onChange={(e) => setLocalCity(e.target.value)}
                  placeholder="Dallas, Miami..."
                  style={{
                    width: '100%', height: 36, padding: '0 12px', marginTop: 4,
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                    fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
              {/* NAICS Code (business only) */}
              {table === 'business' && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>NAICS Code</label>
                  <input
                    type="text"
                    value={localNaics}
                    onChange={(e) => setLocalNaics(e.target.value)}
                    placeholder="e.g. 722511 (full or partial code)"
                    style={{
                      width: '100%', height: 36, padding: '0 12px', marginTop: 4,
                      background: 'var(--bg-2)', border: '1px solid var(--border)',
                      borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                      fontFamily: 'JetBrains Mono, monospace', outline: 'none',
                    }}
                  />
                </div>
              )}
              {/* Industry — multi-select autocomplete (business only) */}
              {table === 'business' && (
                <div className="industry-picker" style={{ gridColumn: '1 / -1', position: 'relative' }}>
                  <label style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Industry {localIndustries.length > 0 && (
                      <span style={{ marginLeft: 6, color: '#f59e0b', textTransform: 'none' }}>
                        {localIndustries.length} selected
                      </span>
                    )}</span>
                    <span style={{ textTransform: 'none', fontSize: 10, color: 'var(--tx-4)' }}>
                      Click to browse · type to search · pick multiple
                    </span>
                  </label>
                  {/* Selected industries as removable chips */}
                  {localIndustries.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      {localIndustries.map((name) => (
                        <span
                          key={name}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: 'rgba(245, 158, 11, .15)',
                            border: '1px solid rgba(245, 158, 11, .4)',
                            borderRadius: 14, padding: '4px 10px',
                            fontSize: 12, color: '#f59e0b',
                            fontFamily: 'inherit',
                          }}
                        >
                          {name}
                          <button
                            onClick={() => removeIndustry(name)}
                            style={{
                              background: 'none', border: 'none', color: '#f59e0b',
                              cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1,
                              fontFamily: 'inherit',
                            }}
                            aria-label={`Remove ${name}`}
                          >
                            {"\u2715"}
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input
                    type="text"
                    value={industryInput}
                    onChange={(e) => { setIndustryInput(e.target.value); setShowIndustryDropdown(true); }}
                    onFocus={() => setShowIndustryDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && industryInput.trim()) {
                        e.preventDefault();
                        addIndustry(industryInput.trim());
                        setShowIndustryDropdown(false);
                      }
                    }}
                    placeholder={localIndustries.length > 0 ? "Add another industry..." : "Click to browse, or type to search..."}
                    style={{
                      width: '100%', height: 36, padding: '0 12px', marginTop: 6,
                      background: 'var(--bg-2)', border: '1px solid var(--border)',
                      borderRadius: 7, color: 'var(--tx-1)', fontSize: 13,
                      fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  {showIndustryDropdown && (
                    <div
                      style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                        background: 'var(--bg-1)', border: '1px solid var(--border)',
                        borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.4)',
                        marginTop: 4, maxHeight: 320, overflowY: 'auto',
                      }}
                    >
                      {industriesLoading && (
                        <div style={{ padding: 12, fontSize: 12, color: 'var(--tx-4)', textAlign: 'center' }}>
                          Loading industries...
                        </div>
                      )}
                      {!industriesLoading && industriesList.length === 0 && (
                        <div style={{ padding: 12, fontSize: 12, color: 'var(--tx-4)', textAlign: 'center' }}>
                          {industryInput ? `No industries match "${industryInput}"` : "No industries available"}
                        </div>
                      )}
                      {!industriesLoading && industriesList.map((ind) => {
                        const alreadyPicked = localIndustries.includes(ind.name);
                        return (
                          <div
                            key={ind.name}
                            onClick={() => {
                              if (!alreadyPicked) addIndustry(ind.name);
                              else removeIndustry(ind.name);
                            }}
                            style={{
                              padding: '8px 12px', cursor: 'pointer',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              borderBottom: '1px solid var(--border)', fontSize: 13,
                              background: alreadyPicked ? 'rgba(245,158,11,.08)' : 'transparent',
                            }}
                            onMouseEnter={(e) => { if (!alreadyPicked) e.currentTarget.style.background = 'var(--bg-2)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = alreadyPicked ? 'rgba(245,158,11,.08)' : 'transparent'; }}
                          >
                            <span style={{ color: 'var(--tx-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                              {alreadyPicked && <span style={{ color: '#f59e0b' }}>{"\u2713"}</span>}
                              {ind.name}
                            </span>
                            <span style={{ color: 'var(--tx-4)', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', flexShrink: 0 }}>
                              {fmtN(ind.count)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 8, lineHeight: 1.5 }}>
              Total updates automatically as you change filters. {table === 'business' && "Pick multiple industries — they're combined with OR."}
              {hasActiveLocalFilters && currentOffset > 0 && (
                <span style={{ color: '#f59e0b', display: 'block', marginTop: 2 }}>
                  {"\u26A0"} Filters changed — progress reset to row 1.
                </span>
              )}
            </div>
          </div>

          {/* BATCH SIZE */}
          {!allDone && (
            <div className="ep-s">
              <div className="ep-st">Download Size (per batch)</div>
              <div className="ep-rw">
                {batchPresets.map(n => (
                  <div key={n} className={"ep-ro " + (batchSize === n && !customBatch ? "on" : "")}
                    onClick={() => { setBatchSize(n); setCustomBatch(""); }}>
                    {fmtN(n)}
                  </div>
                ))}
              </div>
              <div className="ep-ir" style={{ marginTop: 8 }}>
                <span>Custom:</span>
                <input type="number" placeholder="75000" value={customBatch} onChange={e => setCustomBatch(e.target.value)} />
                <span>rows per batch</span>
              </div>
            </div>
          )}

          {/* QUICK COLUMN PRESETS */}
          <div className="ep-s">
            <div className="ep-st">What To Export</div>
            <div className="ep-quick">
              <div className="ep-qb" onClick={() => setSelCols(new Set(emailCol(columns)))}>📧 Emails Only</div>
              <div className="ep-qb" onClick={() => setSelCols(new Set(nameEmailCols(columns)))}>👤 Names + Emails</div>
              <div className="ep-qb" onClick={() => setSelCols(new Set(nameEmailPhoneCols(columns)))}>📞 + Phones</div>
              <div className="ep-qb" onClick={() => setSelCols(new Set(nameEmailPhoneLocCols(columns)))}>📍 + Location</div>
              <div className="ep-qb" onClick={() => setSelCols(new Set(columns))}>📋 Everything</div>
            </div>
          </div>

          {/* COLUMNS — collapsed by default */}
          <div className="ep-s">
            <div className="ep-st" style={{ cursor: "pointer" }} onClick={() => setShowAdvanced(!showAdvanced)}>
              <span>{showAdvanced ? "\u25BE" : "\u25B8"} Columns ({selCols.size}/{columns.length})</span>
              <div className="ep-cb">
                <button className="ep-cbtn" onClick={e => { e.stopPropagation(); setSelCols(new Set(columns)); }}>All</button>
                <button className="ep-cbtn" onClick={e => { e.stopPropagation(); setSelCols(new Set()); }}>None</button>
              </div>
            </div>
            {showAdvanced && (
              <>
                {columns.length > 20 && (
                  <input type="text" value={colSearch} onChange={e => setColSearch(e.target.value)}
                    placeholder="Search columns..." style={{
                      width: "100%", height: 34, padding: "0 12px", background: "var(--bg-2)",
                      border: "1px solid var(--border)", borderRadius: 7, color: "var(--tx-1)",
                      fontSize: 13, fontFamily: "Outfit", outline: "none", marginBottom: 8, marginTop: 8,
                    }}
                  />
                )}
                <div className="ep-cols" style={{ marginTop: 8 }}>
                  {filteredCols.map(c => (
                    <label key={c} className={"ep-col " + (selCols.has(c) ? "on" : "")}>
                      <input type="checkbox" checked={selCols.has(c)} onChange={() => toggleCol(c)} />
                      {niceCol(c)}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* DOWNLOAD LOG */}
          {log.length > 0 && (
            <div className="ep-s">
              <div className="ep-st">
                Download History <span className="ep-log-badge">{log.length} batch{log.length !== 1 ? "es" : ""}</span>
                <button className="ep-log-clr" onClick={resetProgress}>Reset All</button>
              </div>
              {log.map(h => (
                <div className="ep-log-item" key={h.id} onClick={() => setExpanded(expanded === h.id ? null : h.id)}>
                  <div className="ep-log-main">
                    <div className="ep-log-l">
                      <span className="ep-log-row">Batch #{h.batch}: Rows {fmtN(h.from + 1)} {"\u2192"} {fmtN(h.to)} ({fmtN(h.rows)} rows)</span>
                      <span className="ep-log-sub">{h.date} at {h.time} {"\u00B7"} {h.colList.join(", ")}{h.cols > 5 ? ` +${h.cols - 5}` : ""} {"\u00B7"} ~{h.estSize}</span>
                    </div>
                    <div className="ep-log-r">
                      <div className="ep-log-ck">{"\u2713"} Done</div>
                    </div>
                  </div>
                  {expanded === h.id && (
                    <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)", background: "var(--bg-3)", fontSize: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {h.firstRow && (
                        <div>
                          <div style={{ color: "var(--tx-4)", fontSize: 10, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 3 }}>First Row (#{h.from + 1})</div>
                          <div style={{ color: "var(--tx-2)", fontFamily: "JetBrains Mono", fontSize: 12, lineHeight: 1.6, wordBreak: "break-all" }}>
                            {h.firstRow.name}<br />{h.firstRow.email}<br />{h.firstRow.phone}<br />{h.firstRow.loc}
                          </div>
                        </div>
                      )}
                      {h.lastRow && (
                        <div>
                          <div style={{ color: "var(--tx-4)", fontSize: 10, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 3 }}>Last Row (#{h.to})</div>
                          <div style={{ color: "var(--tx-2)", fontFamily: "JetBrains Mono", fontSize: 12, lineHeight: 1.6, wordBreak: "break-all" }}>
                            {h.lastRow.name}<br />{h.lastRow.email}<br />{h.lastRow.phone}<br />{h.lastRow.loc}
                          </div>
                        </div>
                      )}
                      {!h.firstRow && !h.lastRow && (
                        <div style={{ color: "var(--tx-4)", gridColumn: "1/3" }}>Preview not available</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div className="ep-log-total">
                <span>Total downloaded so far:</span>
                <b>{fmtN(totalDL)} rows</b>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="ep-f">
          <div className="ep-sum">
            {recountLoading ? (
              <span style={{ color: "var(--tx-3)" }}>{"\u23F3"} Calculating count for new filter...</span>
            ) : countFailed ? (
              <span style={{ color: "var(--tx-3)" }}>
                Count unknown — but you can still download. Click <b>Export Everything</b> to stream all matching rows.
              </span>
            ) : allDone ? (
              <span style={{ color: "var(--grn)", fontWeight: 600 }}>{"\uD83C\uDF89"} All done!</span>
            ) : (
              <>
                {currentOffset > 0 ? "Next: " : ""}Batch #{batchNum} — <b>{fmtN(actual)}</b> rows {"\u00D7"} <b>{selCols.size}</b> columns
                {estSize > 0 && <> (~<b>{fmtBytes(estSize)}</b>)</>}
                <br />Rows <b>{fmtN(currentOffset + 1)}</b> {"\u2192"} <b>{fmtN(currentOffset + actual)}</b>
                {rem - actual > 0 && (
                  <><br /><span style={{ color: "var(--tx-4)" }}>({fmtN(rem - actual)} remaining · ~{Math.ceil((rem - actual) / effBatch)} more batch{Math.ceil((rem - actual) / effBatch) !== 1 ? "es" : ""})</span></>
                )}
              </>
            )}
          </div>
          {/* Batch download — disabled if we know there are 0 rows or
              still calculating, but ENABLED if count failed (server will
              stream whatever matches). */}
          <button
            className="ep-dl"
            onClick={() => handleDL()}
            disabled={
              selCols.size === 0 ||
              downloading ||
              allDone ||
              recountLoading ||
              (!countFailed && actual <= 0)
            }
          >
            {recountLoading ? "\u23F3 Calculating filtered count..." :
             downloading ? "\u23F3 Downloading..." :
             allDone ? "\uD83C\uDF89 All Downloaded" :
             countFailed ? `\u2B07 Download ~${fmtN(effBatch)} Rows (count unknown)` :
             currentOffset > 0 ? `\u2B07 Download Next ${fmtN(actual)}` :
             `\u2B07 Download ${fmtN(actual)} Rows`}
          </button>

          {/* EXPORT EVERYTHING — always lets user attempt full export.
              Server streams every matching row regardless of count. */}
          <button
            onClick={() => {
              const knownCount = !countFailed && effTotal > 0;
              const msg = knownCount
                ? `Download ALL ${totalIsExact ? '' : 'at least '}${fmtN(effTotal)} rows in one CSV?\n\n` +
                  `Estimated size: ~${Math.round((effTotal * selCols.size * 100) / (1024 * 1024)).toLocaleString()} MB\n` +
                  `This streams the entire result set in one file. Large datasets may take a while.`
                : `Download ALL matching rows in one CSV?\n\n` +
                  `(Count couldn't be calculated, but the server will stream every row that matches your filters.)`;
              if (confirm(msg)) handleDL({ all: true });
            }}
            disabled={selCols.size === 0 || downloading || recountLoading}
            style={{
              width: '100%', marginTop: 10, height: 44,
              background: 'linear-gradient(90deg, rgba(96,165,250,.15), rgba(167,139,250,.15))',
              color: 'var(--tx-1)',
              border: '1px solid var(--blue)',
              borderRadius: 10, fontSize: 14, fontWeight: 700,
              cursor: (selCols.size === 0 || downloading || recountLoading) ? 'not-allowed' : 'pointer',
              opacity: (selCols.size === 0 || downloading || recountLoading) ? 0.5 : 1,
              fontFamily: 'inherit',
            }}
          >
            {recountLoading
              ? `\u23F3 Calculating filtered count...`
              : countFailed
                ? `\uD83D\uDE80 Export Everything (count unknown — streams all matching rows)`
                : `\uD83D\uDE80 Export Everything (${totalIsExact ? '' : '~'}${fmtN(effTotal)} rows in 1 file)`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══ DATA VIEW ═══ */
function DataView({ table }) {
  const [q, setQ] = useState("");
  const [dQ, setDQ] = useState("");
  const [searchCol, setSearchCol] = useState("");
  const [stateF, setStateF] = useState("");
  const [cityF, setCityF] = useState("");
  const [industryF, setIndustryF] = useState("");
  const [naicsF, setNaicsF] = useState("");
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [visCols, setVisCols] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showExport, setShowExport] = useState(false);
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  const [showColDD, setShowColDD] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [toast, setToast] = useState("");
  const [savedFilters, setSavedFilters] = useState([]);
  const searchRef = useRef(null);
  const debRef = useRef(null);
  const [exportProgress, setExportProgress] = useState(() => {
    try { const s = localStorage.getItem(`export_progress_${table}`); if (s) return JSON.parse(s); } catch (e) {} return null;
  });
  function refreshExportProgress() {
    try { const s = localStorage.getItem(`export_progress_${table}`); if (s) setExportProgress(JSON.parse(s)); else setExportProgress(null); } catch (e) {}
  }
  function closeExport() { setShowExport(false); refreshExportProgress(); }

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => setDQ(q), 300);
    return () => clearTimeout(debRef.current);
  }, [q]);
  useEffect(() => {
    setPage(1);
  }, [dQ, searchCol, stateF, cityF, industryF, naicsF, sortBy, sortDir, pageSize]);
  useEffect(() => {
    if (!showColDD) return;
    const h = () => setShowColDD(false);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [showColDD]);
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(""), 2500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") {
        if (showExport) {
          setShowExport(false);
          return;
        }
        if (detailRow) {
          setDetailRow(null);
          return;
        }
        if (showColDD) {
          setShowColDD(false);
          return;
        }
      }
      if (
        e.key === "/" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)
      ) {
        e.preventDefault();
        if (searchRef.current) searchRef.current.focus();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [showExport, detailRow, showColDD]);

  useEffect(() => {
    const params = new URLSearchParams({
      table,
      q: dQ,
      searchCol,
      state: stateF,
      city: cityF,
      industry: industryF,
      naics: naicsF,
      sortBy,
      sortDir,
      page: String(page),
      pageSize: String(pageSize),
    });
    setLoading(true);
    setError("");
    fetch("/api/search?" + params)
      .then((r) => {
        // Silently ignore auth errors — _app.js handles redirect on next mount.
        // Hard redirects here caused refresh loops on transient 401s.
        if (r.status === 401) return;
        if (!r.ok) throw new Error("Server error " + r.status);
        return r.json();
      })
      .then((res) => {
        if (!res) return;
        if (res.error) throw new Error(res.error);
        setData(res.data || []);
        setColumns(res.columns || []);
        setPagination(
          res.pagination || {
            page: 1,
            page_size: 50,
            total: 0,
            total_pages: 1,
          },
        );
        if (visCols === null && res.columns) {
          const defs = DEFAULTS[table] || [];
          const vis = defs.filter((c) => res.columns.includes(c));
          setVisCols(new Set(vis.length > 0 ? vis : res.columns.slice(0, 8)));
        }
      })
      .catch((err) => {
        console.error("Search error:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [
    table,
    dQ,
    searchCol,
    stateF,
    cityF,
    industryF,
    naicsF,
    sortBy,
    sortDir,
    page,
    pageSize,
  ]);

  const pg = pagination;
  const dispCols = (() => {
    if (!visCols) return [];
    const defs = DEFAULTS[table] || [];
    const ordered = defs.filter((c) => visCols.has(c) && columns.includes(c));
    const defSet = new Set(defs);
    columns.forEach((c) => {
      if (visCols.has(c) && !defSet.has(c)) ordered.push(c);
    });
    return ordered;
  })();
  const startRow = (pg.page - 1) * pg.page_size + 1;
  const endRow = Math.min(pg.page * pg.page_size, pg.total);

  function handleSort(col) {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  }
  function toggleSelect(id) {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  }
  function selectAll() {
    if (selected.size === data.length) setSelected(new Set());
    else setSelected(new Set(data.map((r) => r.id)));
  }
  function copyRow(row) {
    navigator.clipboard.writeText(
      dispCols.map((c) => niceCol(c) + ": " + (row[c] || "")).join("\n"),
    );
    setToast("Row copied!");
  }
  function copyCell(val) {
    navigator.clipboard.writeText(val || "");
    setToast("Copied!");
  }
  function exportSelected() {
    if (selected.size === 0) return;
    const rows = data.filter((r) => selected.has(r.id));
    const cols = dispCols;
    const csv = [
      cols.map((c) => niceCol(c)).join(","),
      ...rows.map((r) =>
        cols
          .map((c) => {
            let v = (r[c] || "").toString();
            if (v.includes(",") || v.includes('"'))
              v = '"' + v.replace(/"/g, '""') + '"';
            return v;
          })
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "selected_" + selected.size + "_rows.csv";
    a.click();
    URL.revokeObjectURL(url);
    setToast("Exported " + selected.size + " rows!");
  }
  function copySelectedEmails() {
    const emails = data
      .filter((r) => selected.has(r.id))
      .map((r) => r.email || r.email_address)
      .filter(Boolean);
    navigator.clipboard.writeText(emails.join("\n"));
    setToast(emails.length + " emails copied!");
  }
  function saveFilter() {
    const name = prompt("Name this filter:");
    if (name)
      setSavedFilters((p) => [
        ...p,
        { name, q: dQ, searchCol, stateF, cityF, industryF },
      ]);
  }
  function loadFilter(f) {
    setQ(f.q || "");
    setSearchCol(f.searchCol || "");
    setStateF(f.stateF || "");
    setCityF(f.cityF || "");
    setIndustryF(f.industryF || "");
  }

  const af = [];
  if (stateF) af.push({ l: "State: " + stateF, c: () => setStateF("") });
  if (cityF) af.push({ l: "City: " + cityF, c: () => setCityF("") });
  if (industryF)
    af.push({ l: "Industry: " + industryF, c: () => setIndustryF("") });
  if (naicsF) af.push({ l: "NAICS: " + naicsF, c: () => setNaicsF("") });

  function pageButtons() {
    const b = [];
    let s = Math.max(1, pg.page - 2),
      e = Math.min(pg.total_pages, pg.page + 2);
    if (s > 1) {
      b.push(1);
      if (s > 2) b.push("\u2026");
    }
    for (let i = s; i <= e; i++) b.push(i);
    if (e < pg.total_pages) {
      if (e < pg.total_pages - 1) b.push("\u2026");
      b.push(pg.total_pages);
    }
    return b;
  }

  const currentFilters = {
    q: dQ,
    searchCol,
    state: stateF,
    city: cityF,
    industry: industryF,
    naics: naicsF,
    sortBy,
    sortDir,
  };

  return (
    <>
      <div className="toolbar">
        <div className="fg grow">
          <label>Search</label>
          <div className="si">
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, email, city, phone \u2014 press / to focus"
              autoFocus
            />
          </div>
        </div>
        {/* All inline filters moved to the Filters popup below.
            Only the Search input stays inline because it's the primary action. */}
        {(() => {
          const activeCount = [searchCol, stateF, cityF, industryF, naicsF].filter(Boolean).length;
          return (
            <button
              className={"b " + (activeCount > 0 ? "b-blue" : "b-gray")}
              onClick={() => setShowFiltersPopup(true)}
              style={{ position: 'relative' }}
            >
              {"\uD83D\uDD0E"} Filters
              {activeCount > 0 && (
                <span style={{
                  marginLeft: 6, padding: '1px 7px', borderRadius: 10,
                  background: 'rgba(96,165,250,.25)', fontSize: 11, fontWeight: 700,
                  fontFamily: 'JetBrains Mono, monospace',
                }}>{activeCount}</span>
              )}
            </button>
          );
        })()}
        <div className="dd-wrap" onClick={(e) => e.stopPropagation()}>
          <button className="b b-ac" onClick={() => setShowColDD(!showColDD)}>
            {"\u229E"} Columns
          </button>
          {showColDD && visCols && (
            <ColDD
              allCols={columns}
              visCols={visCols}
              setVisCols={setVisCols}
            />
          )}
        </div>
        <button className="b b-grn" onClick={() => setShowExport(true)}>
          {"\u2B07"} Export
          {exportProgress && exportProgress.offset > 0 && pg.total > 0 && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 4,
              fontSize: 9, fontFamily: "JetBrains Mono, monospace",
              background: "rgba(52,211,153,.15)", color: "var(--grn)",
              padding: "2px 6px", borderRadius: 6, fontWeight: 600,
            }}>
              {Math.round(exportProgress.offset / pg.total * 100)}%
            </span>
          )}
        </button>
        {(dQ || stateF || cityF || industryF || naicsF) && (
          <button className="b b-blue" onClick={saveFilter}>
            {"\uD83D\uDCBE"} Save
          </button>
        )}
        {(q || stateF || cityF || industryF || naicsF) && (
          <button
            className="b b-gray"
            onClick={() => {
              setQ("");
              setStateF("");
              setCityF("");
              setIndustryF("");
              setNaicsF("");
            }}
          >
            {"\u2715"}
          </button>
        )}
      </div>

      {savedFilters.length > 0 && (
        <div className="sf-list">
          {savedFilters.map((f, i) => (
            <span className="sf-chip" key={i} onClick={() => loadFilter(f)}>
              {f.name}
              <span
                className="x"
                onClick={(e) => {
                  e.stopPropagation();
                  setSavedFilters((p) => p.filter((_, j) => j !== i));
                }}
              >
                {"\u2715"}
              </span>
            </span>
          ))}
        </div>
      )}

      {af.length > 0 && (
        <div className="chips">
          {af.map((f, i) => (
            <span className="chip" key={i}>
              {f.l}
              <span className="x" onClick={f.c}>
                {"\u2715"}
              </span>
            </span>
          ))}
        </div>
      )}

      {selected.size > 0 && (
        <div className="sel-bar">
          <b>{selected.size}</b> rows selected
          <button
            className="b b-grn"
            style={{ height: 30, fontSize: 12 }}
            onClick={exportSelected}
          >
            {"\u2B07"} Export CSV
          </button>
          <button
            className="b b-blue"
            style={{ height: 30, fontSize: 12 }}
            onClick={copySelectedEmails}
          >
            {"\uD83D\uDCE7"} Copy Emails
          </button>
          <button
            className="b b-gray"
            style={{ height: 30, fontSize: 12 }}
            onClick={() => setSelected(new Set())}
          >
            {"\u2715"} Clear
          </button>
        </div>
      )}

      {error && (
        <div className="err-banner">
          {"\u26A0\uFE0F"} {error}
        </div>
      )}

      <div className="meta">
        <div className="meta-left">
          {pg.total > 0 ? (
            <>
              Showing <b>{startRow.toLocaleString()}</b>
              {"\u2013"}
              <b>{endRow.toLocaleString()}</b> of{" "}
              <b>{pg.total.toLocaleString()}</b>
            </>
          ) : loading ? (
            "Loading..."
          ) : (
            "No results"
          )}
          {dQ && (
            <>
              {" "}
              matching &quot;<b>{dQ}</b>&quot;
            </>
          )}
          {loading && pg.total > 0 && (
            <span style={{ marginLeft: 8, color: "var(--ac)", fontSize: 12 }}>
              {"\u27F3"}
            </span>
          )}
        </div>
        <div className="meta-right">
          Rows per page:
          <select
            value={pageSize}
            onChange={(e) => setPageSize(+e.target.value)}
          >
            {[25, 50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tw" style={{ position: "relative" }}>
        {loading && data.length === 0 && (
          <div className="loading-overlay">
            <div className="spinner" />
          </div>
        )}
        <div className="ts">
          <table>
            <thead>
              <tr>
                <th className="ck-th">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selected.size === data.length}
                    onChange={selectAll}
                  />
                </th>
                <th style={{ width: 44 }}>#</th>
                {dispCols.map((col) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className={sortBy === col ? "sorted" : ""}
                  >
                    {niceCol(col)}
                    <span className="arr">
                      {sortBy === col
                        ? sortDir === "asc"
                          ? "\u25B2"
                          : "\u25BC"
                        : "\u21C5"}
                    </span>
                  </th>
                ))}
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={dispCols.length + 3}
                    style={{
                      textAlign: "center",
                      padding: 50,
                      color: "var(--tx-4)",
                      fontSize: 15,
                    }}
                  >
                    <div
                      style={{ fontSize: 34, marginBottom: 8, opacity: 0.3 }}
                    >
                      {"\uD83D\uDCED"}
                    </div>
                    {dQ || stateF || cityF
                      ? "No results found \u2014 try different filters"
                      : "No data"}
                  </td>
                </tr>
              ) : (
                data.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={selected.has(row.id) ? "selected" : ""}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggleSelect(row.id)}
                      />
                    </td>
                    <td className="row-num">{startRow + idx}</td>
                    {dispCols.map((col) => (
                      <td
                        key={col}
                        className={
                          col === "email" || col === "email_address" ? "em" : ""
                        }
                        onClick={() => {
                          if (COPY_COLS.has(col) && row[col])
                            copyCell(row[col]);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          copyCell(row[col]);
                        }}
                        style={{
                          cursor:
                            COPY_COLS.has(col) && row[col]
                              ? "pointer"
                              : "default",
                        }}
                        title={
                          COPY_COLS.has(col)
                            ? "Click to copy"
                            : "Right-click to copy"
                        }
                      >
                        {dQ && (searchCol ? col === searchCol : true) ? (
                          <HL text={row[col]} term={dQ} />
                        ) : (
                          row[col] || "\u2014"
                        )}
                      </td>
                    ))}
                    <td>
                      <span style={{ display: "flex", gap: 4 }}>
                        <button
                          className="b b-gray"
                          style={{ height: 26, padding: "0 8px", fontSize: 11 }}
                          onClick={() => setDetailRow(row)}
                        >
                          {"\uD83D\uDC41"}
                        </button>
                        <button
                          className="b b-gray"
                          style={{ height: 26, padding: "0 8px", fontSize: 11 }}
                          onClick={() => copyRow(row)}
                        >
                          {"\uD83D\uDCCB"}
                        </button>
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pg.total_pages > 1 && (
          <div className="pager">
            <span className="pager-info">
              Page {pg.page.toLocaleString()} of{" "}
              {pg.total_pages.toLocaleString()}
            </span>
            <div className="pager-btns">
              <button
                className="pg-btn"
                disabled={pg.page === 1}
                onClick={() => setPage(1)}
              >
                {"\u27E8\u27E8"}
              </button>
              <button
                className="pg-btn"
                disabled={pg.page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {"\u27E8"}
              </button>
              {pageButtons().map((p, i) =>
                p === "\u2026" ? (
                  <span
                    key={i}
                    style={{
                      padding: "0 4px",
                      color: "var(--tx-4)",
                      fontSize: 11,
                    }}
                  >
                    {"\u2026"}
                  </span>
                ) : (
                  <button
                    key={i}
                    className={"pg-btn " + (p === pg.page ? "on" : "")}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                className="pg-btn"
                disabled={pg.page === pg.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                {"\u27E9"}
              </button>
              <button
                className="pg-btn"
                disabled={pg.page === pg.total_pages}
                onClick={() => setPage(pg.total_pages)}
              >
                {"\u27E9\u27E9"}
              </button>
            </div>
          </div>
        )}
      </div>

      {detailRow && (
        <DetailPanel
          row={detailRow}
          columns={columns}
          onClose={() => setDetailRow(null)}
          onToast={setToast}
        />
      )}
      {showExport && (
        <ExportPanel
          table={table}
          columns={columns}
          totalMatch={pg.total}
          filters={currentFilters}
          pageSize={pageSize}
          onClose={closeExport}
          onToast={setToast}
        />
      )}
      {showFiltersPopup && (
        <FiltersPopup
          table={table}
          columns={columns}
          searchCol={searchCol}
          setSearchCol={setSearchCol}
          stateF={stateF}
          setStateF={setStateF}
          cityF={cityF}
          setCityF={setCityF}
          industryF={industryF}
          setIndustryF={setIndustryF}
          naicsF={naicsF}
          setNaicsF={setNaicsF}
          onClose={() => setShowFiltersPopup(false)}
          onClearAll={() => {
            setSearchCol("");
            setStateF("");
            setCityF("");
            setIndustryF("");
            setNaicsF("");
          }}
        />
      )}
      <Toast msg={toast} />
      <div className="kb-hint">
        / = search {"\u00B7"} Esc = close {"\u00B7"} Click email/phone to copy{" "}
        {"\u00B7"} Right-click any cell to copy
      </div>
    </>
  );
}

/* ═══ APP ═══ */
export default function App() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState("business");
  const [stats, setStats] = useState({ business: 0, consumer: 0 });
  useEffect(() => {
    fetch("/api/stats")
      .then((r) => {
        // Silently ignore — _app.js handles auth redirect on remount.
        if (r.status === 401) return;
        return r.json();
      })
      .then((s) => { if (s) setStats(s); })
      .catch(() => {});
  }, []);

  async function handleLogout() {
    try { await fetch('/api/auth/logout', { method: 'POST' }); } catch (e) {}
    router.replace('/login');
  }

  return (
    <>
      <Head>
        <title>MT Emails</title>
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>{`
          @keyframes successPulse { 0%{opacity:0} 10%{opacity:1} 80%{opacity:1} 100%{opacity:0} }
          @keyframes successBounce { 0%{transform:scale(0)} 50%{transform:scale(1.3)} 100%{transform:scale(1)} }
        `}</style>
      </Head>
      <div className="shell">
        <nav className="sidebar">
          <div className="brand">
            <small>Private Platform</small>
          </div>
          <div className="nav">
            <button
              className={"nav-btn " + (activeTab === "business" ? "on" : "")}
              onClick={() => setActiveTab("business")}
            >
              <span className="ic">{"\u25C8"}</span>
              <span>Business</span>
              <span className="nav-count">{fmtN(stats.business || 0)}</span>
            </button>
            <button
              className={"nav-btn " + (activeTab === "consumer" ? "on" : "")}
              onClick={() => setActiveTab("consumer")}
            >
              <span className="ic">{"\u25C7"}</span>
              <span>Consumers</span>
              <span className="nav-count">{fmtN(stats.consumer || 0)}</span>
            </button>
          </div>
          <div style={{ marginTop: 'auto', padding: '16px' }}>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '8px 14px',
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--tx-3)',
                fontSize: '13px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Logout
            </button>
          </div>
        </nav>
        <div className="main">
          <div className="topbar">
            <h2>
              {activeTab === "business"
                ? "Business & Professional Leads"
                : "Consumer Leads"}
            </h2>
            <span className="badge live">{"\u25CF"} LIVE</span>
          </div>
          <div className="content">
            {activeTab === "business" && (
              <DataView key="business" table="business" />
            )}
            {activeTab === "consumer" && (
              <DataView key="consumer" table="consumer" />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
